/**
 * TableAdmin · Concierge® observability dashboard for /table.
 *
 * Founder direction (5 Jun 2026 morning):
 *   - "route-watch panel — yes, build it. The Google Reserve tier's
 *      hit-rate visibility is exactly what I want."
 *   - "curation-watch panel — yes, build it too. Same discipline,
 *      nothing sends unapproved."
 *
 * Two read-only feeds, both already shipped on the backend:
 *   GET /api/table/admin/route-watch      → routing-ladder telemetry
 *   GET /api/table/admin/curation-watch   → Magic Curation draft state
 *
 * Same Concierge® session gate as /table/curate (uses the existing
 * `useConsoleAuth` hook). Visually the page stays in the /table
 * paper-on-paper surface so it never feels like a leak into another
 * product.
 */
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useConsoleAuth } from "@/lib/console-auth";
import { fetchRouteWatch, fetchCurationWatch, fetchDishQueryWatch } from "@/lib/table-api";

const PAGE_BG = "var(--t-paper)";


export default function TableAdmin() {
    const navigate = useNavigate();
    const { user, loading: authLoading } = useConsoleAuth();

    useEffect(() => {
        if (authLoading) return;
        if (!user) navigate("/mira/console/login?return=/table/admin", { replace: true });
    }, [user, authLoading, navigate]);

    if (authLoading || !user) {
        return (
            <main
                style={{
                    minHeight: "100vh", background: PAGE_BG,
                    color: "var(--t-ink2)", display: "flex",
                    alignItems: "center", justifyContent: "center",
                }}
            >
                <span
                    className="t-mono uppercase"
                    style={{ fontSize: 11, letterSpacing: "0.22em" }}
                >
                    · checking access
                </span>
            </main>
        );
    }

    return <Dashboard user={user} />;
}


function Dashboard({ user }) {
    return (
        <main
            data-testid="table-admin-dashboard"
            style={{ minHeight: "100vh", background: PAGE_BG, color: "var(--t-ink)" }}
        >
            <Header userName={user?.name || user?.email} />
            <div className="mx-auto px-6 py-8" style={{ maxWidth: 1100 }}>
                <RouteWatchPanel />
                <div style={{ height: 36 }} />
                <DishQueryWatchPanel />
                <div style={{ height: 36 }} />
                <CurationWatchPanel />
                <p
                    className="t-mono uppercase text-center"
                    style={{
                        fontSize: 10, letterSpacing: "0.18em",
                        color: "var(--t-ink3)", marginTop: 28,
                        fontStyle: "italic",
                    }}
                    data-testid="admin-firewall-line"
                >
                    read-only · the dossier never sends until approved
                </p>
            </div>
        </main>
    );
}


function Header({ userName }) {
    return (
        <header
            className="px-6 py-5 flex items-center justify-between"
            style={{
                borderBottom: "1px solid var(--t-line)",
                background: "var(--t-paper2)",
            }}
        >
            <div className="flex items-center gap-3 flex-wrap">
                <span
                    className="t-mono uppercase"
                    style={{
                        fontSize: 10, letterSpacing: "0.24em",
                        color: "var(--t-brass)", fontWeight: 700,
                    }}
                >
                    ✦ /table · Observability Console
                </span>
                <span
                    className="t-mono"
                    style={{ fontSize: 9, letterSpacing: "0.18em", color: "var(--t-ink3)" }}
                >
                    · routing ladder · curation drafts
                </span>
            </div>
            <div className="flex items-center gap-4 flex-wrap">
                <a
                    href="/table/curate"
                    data-testid="admin-link-to-curate"
                    className="t-mono uppercase"
                    style={{
                        fontSize: 10, letterSpacing: "0.18em",
                        color: "var(--t-ink2)", textDecoration: "none",
                        borderBottom: "1px solid var(--t-line2)",
                        paddingBottom: 2,
                    }}
                >
                    ↗ studio
                </a>
                {userName ? (
                    <span
                        className="t-mono"
                        style={{ fontSize: 11, color: "var(--t-ink2)" }}
                        data-testid="admin-user-name"
                    >
                        {userName}
                    </span>
                ) : null}
            </div>
        </header>
    );
}


// ─────────────────────────────────────────────────────────────────────
// DISH-QUERY-WATCH · unmet-demand telemetry (founder ear-test
//                    5 Jun 2026 night). Every dish-intent banner
//                    shown and every Concierge® click-through pings
//                    here. The panel surfaces the highest-signal
//                    growth data we have · what members actually
//                    want that we can't yet show.
// ─────────────────────────────────────────────────────────────────────


function DishQueryWatchPanel() {
    const [hours, setHours] = useState(168);   // default: 7d window
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const reload = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const fresh = await fetchDishQueryWatch({ hours, limit: 30 });
            setData(fresh);
        } catch (e) {
            setError(e?.message || "Failed to load");
        } finally {
            setLoading(false);
        }
    }, [hours]);

    useEffect(() => { reload(); }, [reload]);

    return (
        <section data-testid="dish-query-watch-panel">
            <PanelHeader
                eyebrow="dish-search unmet demand"
                title="What members asked us · that we couldn't show"
                rightSlot={
                    <div className="flex items-center gap-2">
                        <WindowPicker hours={hours} setHours={setHours} />
                        <ReloadButton onClick={reload} loading={loading} testId="dish-query-watch-reload" />
                    </div>
                }
            />
            {error ? (
                <ErrorRow message={error} />
            ) : loading && !data ? (
                <LoadingRow label="reading dish telemetry" />
            ) : !data || data.total === 0 ? (
                <EmptyRow message="no dish-intent pings in this window · nothing to triage yet" />
            ) : (
                <>
                    <div
                        className="grid grid-cols-3 gap-3 mt-4"
                        data-testid="dish-query-watch-summary"
                    >
                        <StatCard
                            testId="dish-stat-total"
                            label="Total pings"
                            primary={data.total.toLocaleString()}
                            secondary={windowLabel(data.window_hours)}
                        />
                        <StatCard
                            testId="dish-stat-unique-dishes"
                            label="Unique dishes"
                            primary={(data.by_dish || []).length.toLocaleString()}
                            secondary="distinct asks"
                            tint="brass"
                        />
                        <StatCard
                            testId="dish-stat-unique-cities"
                            label="Unique cities"
                            primary={(data.by_city || []).length.toLocaleString()}
                            secondary="locations queried"
                        />
                    </div>
                    <TopDishesBlock items={data.by_dish || []} />
                    <TopCitiesBlock items={data.by_city || []} />
                </>
            )}
        </section>
    );
}


function TopDishesBlock({ items }) {
    if (!items || items.length === 0) return null;
    const total = items.reduce((s, d) => s + d.count, 0);
    return (
        <div className="mt-5" data-testid="dish-query-watch-by-dish">
            <div
                className="t-mono uppercase mb-2"
                style={{
                    fontSize: 10, letterSpacing: "0.18em",
                    color: "var(--t-ink3)",
                }}
            >
                top dishes · prioritise these for menu-search
            </div>
            <div
                style={{
                    background: "var(--t-paper2)",
                    border: "1px solid var(--t-line2)",
                    borderRadius: 14,
                    overflow: "hidden",
                }}
            >
                {items.slice(0, 10).map((it, i) => {
                    const pct = total > 0
                        ? Math.round((it.count / total) * 100) : 0;
                    return (
                        <div
                            key={it.dish}
                            data-testid={`dish-watch-row-${i}`}
                            style={{
                                borderTop: i === 0 ? "0" : "1px solid var(--t-line2)",
                                padding: "10px 16px",
                                display: "flex",
                                alignItems: "center",
                                gap: 12,
                                flexWrap: "wrap",
                            }}
                        >
                            <span
                                className="t-serif"
                                style={{
                                    fontSize: 15,
                                    color: "var(--t-ink)",
                                    fontWeight: 500,
                                    minWidth: 160,
                                    textTransform: "lowercase",
                                }}
                            >
                                {it.dish}
                            </span>
                            <div
                                style={{
                                    flex: 1,
                                    minWidth: 80,
                                    height: 6, borderRadius: 4,
                                    background: "var(--t-line2)",
                                    position: "relative",
                                    overflow: "hidden",
                                }}
                            >
                                <div
                                    style={{
                                        position: "absolute", left: 0,
                                        top: 0, bottom: 0,
                                        width: `${pct}%`,
                                        background: "var(--t-brass)",
                                        transition: "width 0.4s",
                                    }}
                                />
                            </div>
                            <span
                                className="t-mono"
                                style={{
                                    fontSize: 11,
                                    color: "var(--t-ink3)",
                                    minWidth: 64, textAlign: "right",
                                }}
                            >
                                {it.count} ask{it.count === 1 ? "" : "s"}
                            </span>
                            {it.cities && it.cities.length > 0 ? (
                                <span
                                    className="t-mono"
                                    style={{
                                        fontSize: 10,
                                        color: "var(--t-ink3)",
                                        flexBasis: "100%",
                                        marginTop: 2,
                                        letterSpacing: "0.04em",
                                    }}
                                >
                                    · in {it.cities.join(" · ")}
                                </span>
                            ) : null}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}


function TopCitiesBlock({ items }) {
    if (!items || items.length === 0) return null;
    return (
        <div className="mt-5" data-testid="dish-query-watch-by-city">
            <div
                className="t-mono uppercase mb-2"
                style={{
                    fontSize: 10, letterSpacing: "0.18em",
                    color: "var(--t-ink3)",
                }}
            >
                where the gap is widest · curated-csv priorities
            </div>
            <div
                className="flex flex-wrap gap-2"
            >
                {items.slice(0, 12).map((c) => (
                    <span
                        key={c.city}
                        data-testid={`dish-watch-city-${c.city.toLowerCase().replace(/\W+/g, "-")}`}
                        style={{
                            padding: "5px 12px",
                            borderRadius: 999,
                            background: "var(--t-card)",
                            border: "1px solid var(--t-line2)",
                            fontSize: 12,
                            color: "var(--t-ink2)",
                        }}
                    >
                        {c.city}{" "}
                        <span
                            className="t-mono"
                            style={{
                                fontSize: 10,
                                color: "var(--t-ink3)",
                                marginLeft: 4,
                            }}
                        >
                            · {c.count}
                        </span>
                    </span>
                ))}
            </div>
        </div>
    );
}


// ─────────────────────────────────────────────────────────────────────
// ROUTE-WATCH · routing ladder telemetry
// ─────────────────────────────────────────────────────────────────────


function RouteWatchPanel() {
    const [hours, setHours] = useState(24);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const reload = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const fresh = await fetchRouteWatch({ hours, limit: 50 });
            setData(fresh);
        } catch (e) {
            setError(e?.message || "Failed to load");
        } finally {
            setLoading(false);
        }
    }, [hours]);

    useEffect(() => { reload(); }, [reload]);

    return (
        <section data-testid="route-watch-panel">
            <PanelHeader
                eyebrow="route watch"
                title="Routing ladder · hit-rate"
                rightSlot={
                    <div className="flex items-center gap-2">
                        <WindowPicker hours={hours} setHours={setHours} />
                        <ReloadButton onClick={reload} loading={loading} testId="route-watch-reload" />
                    </div>
                }
            />
            {error ? (
                <ErrorRow message={error} />
            ) : loading && !data ? (
                <LoadingRow label="reading routing log" />
            ) : !data || data.total === 0 ? (
                <EmptyRow message="no resolutions logged in this window" />
            ) : (
                <>
                    <RouteSummaryGrid data={data} />
                    <ViaBreakdown items={data.instant_by_via} totalInstant={
                        (data.by_route || []).find((r) => r.route === "instant")?.count || 0
                    } />
                    <RecentResolutions rows={data.recent || []} />
                </>
            )}
        </section>
    );
}


function RouteSummaryGrid({ data }) {
    const labelMap = {
        instant: "Instant",
        call: "Call",
        contact: "Contact",
        concierge: "Concierge®",
    };
    const total = data.total || 0;
    const byRoute = data.by_route || [];
    return (
        <div
            className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4"
            data-testid="route-watch-by-route"
        >
            {["instant", "call", "contact", "concierge"].map((r) => {
                const row = byRoute.find((b) => b.route === r);
                const count = row?.count || 0;
                const pct = total > 0 ? Math.round((count / total) * 100) : 0;
                return (
                    <StatCard
                        key={r}
                        testId={`route-watch-stat-${r}`}
                        label={labelMap[r] || r}
                        primary={count.toLocaleString()}
                        secondary={`${pct}% · ${windowLabel(data.window_hours)}`}
                    />
                );
            })}
        </div>
    );
}


function ViaBreakdown({ items, totalInstant }) {
    if (!items || items.length === 0) return null;
    return (
        <div
            className="mt-5"
            data-testid="route-watch-via-breakdown"
        >
            <div
                className="t-mono uppercase mb-2"
                style={{
                    fontSize: 10, letterSpacing: "0.18em",
                    color: "var(--t-ink3)",
                }}
            >
                instant · by provider
            </div>
            <div className="space-y-1">
                {items.map((it) => {
                    const pct = totalInstant > 0
                        ? Math.round((it.count / totalInstant) * 100) : 0;
                    const label = it.via || "—";
                    const isGoogleReserve = label === "Google Reserve";
                    return (
                        <div
                            key={label}
                            data-testid={`route-watch-via-${slug(label)}`}
                            className="flex items-center gap-3"
                        >
                            <span
                                style={{
                                    fontSize: 13,
                                    minWidth: 140,
                                    color: isGoogleReserve ? "var(--t-brass)" : "var(--t-ink)",
                                    fontWeight: isGoogleReserve ? 600 : 500,
                                }}
                            >
                                {label}
                            </span>
                            <div
                                style={{
                                    flex: 1,
                                    height: 8, borderRadius: 4,
                                    background: "var(--t-line2)",
                                    position: "relative",
                                    overflow: "hidden",
                                }}
                            >
                                <div
                                    style={{
                                        position: "absolute", left: 0, top: 0, bottom: 0,
                                        width: `${pct}%`,
                                        background: isGoogleReserve
                                            ? "var(--t-brass)" : "var(--t-ink2)",
                                        transition: "width 0.4s",
                                    }}
                                />
                            </div>
                            <span
                                className="t-mono"
                                style={{
                                    fontSize: 11,
                                    color: "var(--t-ink3)",
                                    minWidth: 64, textAlign: "right",
                                }}
                            >
                                {it.count.toLocaleString()} · {pct}%
                            </span>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}


function RecentResolutions({ rows }) {
    if (!rows || rows.length === 0) return null;
    return (
        <div className="mt-6" data-testid="route-watch-recent">
            <div
                className="t-mono uppercase mb-2"
                style={{
                    fontSize: 10, letterSpacing: "0.18em",
                    color: "var(--t-ink3)",
                }}
            >
                recent resolutions · {rows.length}
            </div>
            <div
                style={{
                    background: "var(--t-paper2)",
                    border: "1px solid var(--t-line2)",
                    borderRadius: 14,
                    maxHeight: 280, overflowY: "auto",
                }}
            >
                {rows.slice(0, 30).map((r, i) => (
                    <div
                        key={`${r.place_id || ""}-${r._ts || i}`}
                        data-testid={`route-watch-row-${i}`}
                        className="flex items-center gap-3 px-4 py-2"
                        style={{
                            borderTop: i === 0 ? "0" : "1px solid var(--t-line2)",
                            fontSize: 13,
                        }}
                    >
                        <RoutePill route={r.route} via={r.route_via} />
                        <span
                            style={{
                                color: "var(--t-ink)",
                                flex: 1, minWidth: 0,
                                overflow: "hidden", textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                            }}
                        >
                            {r.venue_name || "—"}
                        </span>
                        {r.city ? (
                            <span
                                className="t-mono"
                                style={{ fontSize: 10, color: "var(--t-ink3)", letterSpacing: "0.04em" }}
                            >
                                {r.city}
                            </span>
                        ) : null}
                        <span
                            className="t-mono"
                            style={{ fontSize: 10, color: "var(--t-ink3)" }}
                        >
                            {formatTs(r._ts)}
                        </span>
                    </div>
                ))}
            </div>
        </div>
    );
}


function RoutePill({ route, via }) {
    const palette = {
        instant: { bg: "var(--t-brass-wash)", fg: "var(--t-brass)" },
        call: { bg: "var(--t-card)", fg: "var(--t-ink2)" },
        contact: { bg: "var(--t-card)", fg: "var(--t-ink2)" },
        concierge: { bg: "var(--t-verified-wash)", fg: "var(--t-verified)" },
    };
    const p = palette[route] || { bg: "var(--t-card)", fg: "var(--t-ink2)" };
    return (
        <span
            className="t-mono uppercase"
            style={{
                fontSize: 9, letterSpacing: "0.14em",
                background: p.bg, color: p.fg,
                padding: "3px 9px", borderRadius: 10,
                whiteSpace: "nowrap",
                minWidth: 100, textAlign: "center",
            }}
        >
            {route || "—"}{via ? ` · ${via}` : ""}
        </span>
    );
}


// ─────────────────────────────────────────────────────────────────────
// CURATION-WATCH · Magic Curation draft state
// ─────────────────────────────────────────────────────────────────────


function CurationWatchPanel() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const reload = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const fresh = await fetchCurationWatch({ limit: 50 });
            setData(fresh);
        } catch (e) {
            setError(e?.message || "Failed to load");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { reload(); }, [reload]);

    return (
        <section data-testid="curation-watch-panel">
            <PanelHeader
                eyebrow="curation watch"
                title="Magic Curation · drafts &amp; approvals"
                rightSlot={
                    <ReloadButton onClick={reload} loading={loading} testId="curation-watch-reload" />
                }
            />
            {error ? (
                <ErrorRow message={error} />
            ) : loading && !data ? (
                <LoadingRow label="reading draft store" />
            ) : !data || data.total === 0 ? (
                <EmptyRow message="no drafts yet · /table/curate is where they appear" />
            ) : (
                <>
                    <div
                        className="grid grid-cols-3 gap-3 mt-4"
                        data-testid="curation-watch-summary"
                    >
                        <StatCard
                            testId="curation-stat-total"
                            label="Total drafts"
                            primary={data.total.toLocaleString()}
                            secondary="all-time"
                        />
                        <StatCard
                            testId="curation-stat-approved"
                            label="Approved"
                            primary={data.approved.toLocaleString()}
                            secondary={`${pctOf(data.approved, data.total)}% of total`}
                            tint="verified"
                        />
                        <StatCard
                            testId="curation-stat-pending"
                            label="Pending"
                            primary={data.pending.toLocaleString()}
                            secondary="approval gate"
                            tint={data.pending > 0 ? "brass" : "ink"}
                        />
                    </div>
                    <CurationDraftList drafts={data.drafts || []} />
                </>
            )}
        </section>
    );
}


function CurationDraftList({ drafts }) {
    if (!drafts || drafts.length === 0) return null;
    return (
        <div className="mt-5" data-testid="curation-watch-list">
            <div
                className="t-mono uppercase mb-2"
                style={{
                    fontSize: 10, letterSpacing: "0.18em",
                    color: "var(--t-ink3)",
                }}
            >
                recent drafts
            </div>
            <div
                style={{
                    background: "var(--t-paper2)",
                    border: "1px solid var(--t-line2)",
                    borderRadius: 14,
                    overflow: "hidden",
                }}
            >
                {drafts.map((d, i) => (
                    <div
                        key={d.draft_id}
                        data-testid={`curation-watch-row-${i}`}
                        style={{
                            borderTop: i === 0 ? "0" : "1px solid var(--t-line2)",
                            padding: "12px 16px",
                        }}
                    >
                        <div className="flex items-center gap-3 flex-wrap">
                            <ApprovalPill approved={!!d.approved} />
                            <span
                                style={{
                                    fontSize: 14, color: "var(--t-ink)",
                                    fontWeight: 600, flex: 1, minWidth: 200,
                                }}
                            >
                                {d.title || "(untitled)"}
                            </span>
                            <span
                                className="t-mono"
                                style={{ fontSize: 10, color: "var(--t-ink3)" }}
                            >
                                {d.picks_count} venue{d.picks_count === 1 ? "" : "s"}
                            </span>
                            <span
                                className="t-mono"
                                style={{ fontSize: 10, color: "var(--t-ink3)" }}
                            >
                                {formatTs(d.created_at)}
                            </span>
                        </div>
                        <div
                            className="mt-1"
                            style={{
                                fontSize: 12,
                                color: "var(--t-ink2)",
                                fontStyle: "italic",
                            }}
                        >
                            {d.brief}
                        </div>
                        <div
                            className="t-mono"
                            style={{
                                fontSize: 9, color: "var(--t-ink3)",
                                marginTop: 4, letterSpacing: "0.06em",
                            }}
                        >
                            {d.draft_id}
                            {d.occasion ? ` · ${d.occasion}` : ""}
                            {d.locale ? ` · ${d.locale}` : ""}
                            {d.approved_at ? ` · approved ${formatTs(d.approved_at)}` : ""}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}


function ApprovalPill({ approved }) {
    return (
        <span
            data-testid={approved ? "approval-pill-approved" : "approval-pill-pending"}
            className="t-mono uppercase"
            style={{
                fontSize: 9, letterSpacing: "0.16em",
                background: approved ? "var(--t-verified-wash)" : "var(--t-brass-wash)",
                color: approved ? "var(--t-verified)" : "var(--t-brass)",
                border: `1px solid ${approved ? "var(--t-verified)" : "var(--t-brass2)"}`,
                padding: "3px 9px", borderRadius: 10,
                whiteSpace: "nowrap",
            }}
        >
            {approved ? "✓ approved" : "· pending"}
        </span>
    );
}


// ─────────────────────────────────────────────────────────────────────
// Primitives
// ─────────────────────────────────────────────────────────────────────


function PanelHeader({ eyebrow, title, rightSlot }) {
    return (
        <div className="flex items-end justify-between gap-3 mb-1 flex-wrap">
            <div>
                <div
                    className="t-mono uppercase"
                    style={{
                        fontSize: 10, letterSpacing: "0.22em",
                        color: "var(--t-brass)", fontWeight: 700,
                    }}
                >
                    {eyebrow}
                </div>
                <h2
                    className="t-serif"
                    style={{
                        fontSize: 26, fontWeight: 500,
                        lineHeight: 1.15, letterSpacing: "-0.01em",
                        color: "var(--t-ink)", marginTop: 4,
                    }}
                >
                    {title}
                </h2>
            </div>
            {rightSlot}
        </div>
    );
}


function WindowPicker({ hours, setHours }) {
    const opts = [
        { v: 1, label: "1h" },
        { v: 24, label: "24h" },
        { v: 168, label: "7d" },
        { v: 720, label: "30d" },
    ];
    return (
        <div
            className="inline-flex"
            data-testid="route-watch-window-picker"
            style={{
                border: "1px solid var(--t-line2)",
                borderRadius: 999,
                background: "var(--t-paper2)",
                padding: 2,
            }}
        >
            {opts.map((o) => {
                const active = hours === o.v;
                return (
                    <button
                        key={o.v}
                        type="button"
                        data-testid={`route-watch-window-${o.label}`}
                        onClick={() => setHours(o.v)}
                        style={{
                            padding: "5px 11px",
                            fontSize: 12, fontWeight: 600,
                            background: active ? "var(--t-ink)" : "transparent",
                            color: active ? "var(--t-paper2)" : "var(--t-ink2)",
                            border: 0, borderRadius: 999,
                            cursor: "pointer",
                            transition: "background 0.15s",
                        }}
                    >
                        {o.label}
                    </button>
                );
            })}
        </div>
    );
}


function ReloadButton({ onClick, loading, testId }) {
    return (
        <button
            type="button"
            data-testid={testId}
            onClick={onClick}
            disabled={loading}
            style={{
                padding: "6px 14px",
                fontSize: 11, fontWeight: 600, letterSpacing: "0.02em",
                background: "transparent",
                color: loading ? "var(--t-ink3)" : "var(--t-ink2)",
                border: "1px solid var(--t-line2)",
                borderRadius: 999,
                cursor: loading ? "wait" : "pointer",
            }}
        >
            {loading ? "·…" : "↻ reload"}
        </button>
    );
}


function StatCard({ label, primary, secondary, testId, tint = "ink" }) {
    const tints = {
        ink: { fg: "var(--t-ink)" },
        brass: { fg: "var(--t-brass)" },
        verified: { fg: "var(--t-verified)" },
    };
    const t = tints[tint] || tints.ink;
    return (
        <div
            data-testid={testId}
            style={{
                background: "var(--t-paper2)",
                border: "1px solid var(--t-line2)",
                borderRadius: 14,
                padding: "14px 16px",
            }}
        >
            <div
                className="t-mono uppercase"
                style={{
                    fontSize: 10, letterSpacing: "0.18em",
                    color: "var(--t-ink3)",
                }}
            >
                {label}
            </div>
            <div
                className="t-serif"
                style={{
                    fontSize: 32, fontWeight: 500,
                    lineHeight: 1.1, marginTop: 6,
                    letterSpacing: "-0.01em",
                    color: t.fg,
                }}
            >
                {primary}
            </div>
            <div
                style={{
                    fontSize: 12, color: "var(--t-ink3)", marginTop: 2,
                }}
            >
                {secondary}
            </div>
        </div>
    );
}


function LoadingRow({ label }) {
    return (
        <div
            className="t-mono uppercase"
            data-testid="admin-loading"
            style={{
                padding: "26px 16px", textAlign: "center",
                color: "var(--t-brass)",
                fontSize: 11, letterSpacing: "0.22em", fontStyle: "italic",
                background: "var(--t-paper2)",
                border: "1px solid var(--t-line2)",
                borderRadius: 14, marginTop: 12,
            }}
        >
            · {label}…
        </div>
    );
}


function EmptyRow({ message }) {
    return (
        <div
            data-testid="admin-empty"
            style={{
                padding: "30px 16px", textAlign: "center",
                background: "var(--t-paper2)",
                border: "1px solid var(--t-line2)",
                borderRadius: 14, marginTop: 12,
            }}
        >
            <p
                className="t-serif italic"
                style={{ fontSize: 14, color: "var(--t-ink2)" }}
            >
                {message}
            </p>
        </div>
    );
}


function ErrorRow({ message }) {
    return (
        <div
            data-testid="admin-error"
            style={{
                padding: "20px 16px",
                background: "var(--t-paper2)",
                border: "1px solid var(--t-line2)",
                borderRadius: 14, marginTop: 12,
                color: "var(--t-ink2)",
                fontSize: 13,
            }}
        >
            <span
                className="t-mono uppercase"
                style={{
                    fontSize: 10, letterSpacing: "0.18em",
                    color: "var(--t-brass)",
                }}
            >
                · load failed ·{" "}
            </span>
            {message}
        </div>
    );
}


// ─────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────


function pctOf(part, total) {
    if (!total) return 0;
    return Math.round((part / total) * 100);
}


function slug(s) {
    return String(s || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
}


function windowLabel(hours) {
    if (!hours) return "—";
    if (hours <= 1) return "last hour";
    if (hours <= 24) return `last ${hours}h`;
    const days = Math.round(hours / 24);
    return `last ${days}d`;
}


function formatTs(iso) {
    if (!iso) return "";
    try {
        const d = new Date(iso);
        const now = new Date();
        const diff = now - d;
        if (diff < 60_000) return "just now";
        if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`;
        if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h ago`;
        const days = Math.floor(diff / 86_400_000);
        if (days < 30) return `${days}d ago`;
        return d.toISOString().slice(0, 10);
    } catch {
        return "";
    }
}
